import React, { useMemo } from 'react';

const HotColdTracker = ({ roster, gameLogs, onPlayerClick }) => {
  const players = useMemo(() => {
    if (!roster?.length || !gameLogs?.length) return { hot: [], cold: [] };

    const scored = roster
      .filter(p => p.position?.type !== 'Pitcher')
      .map(p => {
        const log = gameLogs.find(g => g.id === p.person?.id)?.data;
        const splits = log?.stats?.find(s => s.group?.displayName === 'hitting')?.splits?.slice(0, 7) || [];
        const ab = splits.reduce((a, s) => a + (s.stat?.atBats || 0), 0);
        const h = splits.reduce((a, s) => a + (s.stat?.hits || 0), 0);
        const hr = splits.reduce((a, s) => a + (s.stat?.homeRuns || 0), 0);
        const rbi = splits.reduce((a, s) => a + (s.stat?.rbi || 0), 0);
        const avg = ab > 0 ? h / ab : 0;
        const score = avg * 100 + hr * 10 + rbi * 3;
        return { player: p, avg: avg > 0 ? avg.toFixed(3).replace('0.', '.') : '--', hr, rbi, score, ab };
      })
      .filter(p => p.ab >= 3)
      .sort((a, b) => b.score - a.score);

    return { hot: scored.slice(0, 4), cold: scored.slice(-4).reverse() };
  }, [roster, gameLogs]);

  const PlayerRow = ({ p, type }) => (
    <div onClick={() => onPlayerClick?.(p.player.person?.id, p.player.person?.fullName)}
      className="flex items-center gap-2 py-1.5 border-b border-[rgba(101,141,113,0.2)] last:border-0 cursor-pointer group hover:bg-[rgba(255,255,255,0.03)]">
      <span className={`text-[9px] font-black px-1.5 py-0.5 shrink-0 ${type === 'hot' ? 'bg-[rgba(196,51,62,0.3)] text-[#c4333e]' : 'bg-[rgba(101,141,113,0.2)] text-[#658d71]'}`}>
        {type === 'hot' ? '🔥' : '🧊'}
      </span>
      <span className="text-[11px] font-bold text-white uppercase truncate flex-1 group-hover:text-[#d8ceb3] transition-colors">
        {p.player.person?.fullName?.split(' ').pop()}
      </span>
      <div className="flex items-center gap-2 shrink-0">
        <span className={`text-[10px] font-black tabular-nums ${type === 'hot' ? 'text-[#a9c9b1]' : 'text-[#658d71]'}`}>{p.avg}</span>
        {p.hr > 0 && <span className="text-[9px] text-[#c4333e] font-black">{p.hr}HR</span>}
      </div>
    </div>
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {[{ label: 'Hot Batters', type: 'hot', data: players.hot }, { label: 'Cold Batters', type: 'cold', data: players.cold }].map(({ label, type, data }) => (
        <div key={type} className="border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
          <div className="px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
            <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">{label}</span>
            <p className="text-[9px] text-[#658d71] font-bold uppercase tracking-widest mt-0.5">Last 7 games</p>
          </div>
          <div className="p-3">
            {data.length ? data.map((p, i) => <PlayerRow key={i} p={p} type={type} />) :
              <p className="text-[#658d71] text-xs text-center py-4 uppercase tracking-widest">No data</p>}
          </div>
        </div>
      ))}
    </div>
  );
};

export default HotColdTracker;